/**
 * API Configuration
 * Lambda URL endpoints for different environments
 */

export type Environment = 'development' | 'production';

export interface ApiConfig {
  productsApi: string;
  brandsApi: string;
  xmlMappingsApi: string;
  combinationsApi: string;
  validationApi: string;
  appointmentsApi: string;
}

// Unified Backend Lambda URLs (Updated - CORS fixed)
// Production: Use REACT_APP_PROD_API_URL or default to api.fashcortex.com (NEVER use REACT_APP_API_URL for prod)
const PRODUCTION_BACKEND_URL = process.env.REACT_APP_PROD_API_URL || 'https://api.fashcortex.com';

const developmentConfig: ApiConfig = {
  productsApi: PRODUCTION_BACKEND_URL, // Use production API for local development
  brandsApi: PRODUCTION_BACKEND_URL,
  xmlMappingsApi: PRODUCTION_BACKEND_URL,
  combinationsApi: PRODUCTION_BACKEND_URL,
  validationApi: PRODUCTION_BACKEND_URL,
  appointmentsApi: PRODUCTION_BACKEND_URL,
};

const productionConfig: ApiConfig = {
  productsApi: PRODUCTION_BACKEND_URL,
  brandsApi: PRODUCTION_BACKEND_URL,
  xmlMappingsApi: PRODUCTION_BACKEND_URL,
  combinationsApi: PRODUCTION_BACKEND_URL,
  validationApi: PRODUCTION_BACKEND_URL,
  appointmentsApi: PRODUCTION_BACKEND_URL,
};

export function getEnvironment(): Environment {
  const env = (process.env.REACT_APP_ENV || 'development') as Environment;
  console.log('🔍 getEnvironment() called:', {
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    NODE_ENV: process.env.NODE_ENV,
    resolvedEnv: env
  });
  return env;
}

export function getApiConfig(): ApiConfig {
  const env = getEnvironment();
  
  console.log('🔍 getApiConfig() called:', {
    env,
    PRODUCTION_BACKEND_URL
  });
  
  switch (env) {
    case 'production':
      return productionConfig;
    case 'development':
    default:
      return developmentConfig;
  }
}

export const apiConfig = getApiConfig();

// CRITICAL DEBUG: Log immediately and make it visible
console.log('🔍 apiConfig initialized:', {
  environment: getEnvironment(),
  productsApi: apiConfig.productsApi,
  brandsApi: apiConfig.brandsApi,
  xmlMappingsApi: apiConfig.xmlMappingsApi,
  combinationsApi: apiConfig.combinationsApi,
  validationApi: apiConfig.validationApi,
  appointmentsApi: apiConfig.appointmentsApi,
  ALL_URLS: JSON.stringify(apiConfig, null, 2)
});

// CRITICAL: Verify URL is correct
if (apiConfig.productsApi.includes('lambda-url') || apiConfig.brandsApi.includes('lambda-url')) {
  console.error('❌ ERROR: Old Lambda URL detected in apiConfig!', apiConfig);
  throw new Error('Old Lambda URL detected in apiConfig! This should never happen.');
}

/**
 * Get the base URL for API calls based on the current environment
 */
export function getApiBaseUrl(): string {
  const config = getApiConfig();
  return `${config.productsApi}/api/v1`; // All endpoints use the same unified backend URL with /api/v1 prefix
}

export const API_BASE_URL = getApiBaseUrl();

/**
 * Fetch with automatic authentication token
 * Adds JWT token to all requests automatically
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = localStorage.getItem('token');
  
  console.log('🔐 authenticatedFetch - Token:', token ? `${token.substring(0, 30)}...` : 'NO TOKEN FOUND');
  console.log('🌐 authenticatedFetch - URL:', url);
  
  const headers = {
    ...options.headers,
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // If unauthorized, token might be expired - try to refresh
  if (response.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (refreshToken) {
      try {
        // Try to refresh token
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });

        const refreshData = await refreshResponse.json();

        if (refreshResponse.ok && refreshData.success) {
          // Update tokens
          localStorage.setItem('token', refreshData.token);
          localStorage.setItem('refreshToken', refreshData.refreshToken);

          // Retry original request with new token
          return fetch(url, {
            ...options,
            headers: {
              ...options.headers,
              'Authorization': `Bearer ${refreshData.token}`,
            },
          });
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }

    // If refresh failed, clear tokens
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }

  return response;
}

/**
 * Get the appropriate brand ID based on environment
 * - Development: uses brandId (pointing to production API)
 * - Production: uses brandId
 */
export function getActiveBrandId(user: { brandId?: string; stageBrandId?: string } | null): string | undefined {
  console.log('🔍 getActiveBrandId Debug:', user);
  if (!user) return undefined;
  
  const env = getEnvironment();
  
  // Debug logging
  console.log('🔍 getActiveBrandId Debug:', {
    environment: env,
    'process.env.REACT_APP_ENV': process.env.REACT_APP_ENV,
    'process.env.NODE_ENV': process.env.NODE_ENV,
    brandId: user.brandId,
    stageBrandId: user.stageBrandId,
  });
  
  // Always use brandId (staging is removed)
  console.log(`✅ Using ${env} environment → Selected Brand ID:`, user.brandId);
  return user.brandId;
}

